import { Component, DestroyRef, inject, input, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, ReactiveFormsModule, Validators } from '@angular/forms';

import { IconModule, SimpleInputModule, TextAreaComponentModule } from '@hglobal/fin-ui';
import { FormGroup } from '@ngneat/reactive-forms';
import { GroupResolverFormBuilder } from '@ngneat/reactive-forms/lib/form-builder';
import { TranslateModule } from '@ngx-translate/core';

import { SearchSelectComponent } from '@kr/components';
import { WalletItemComponent } from '@kr/components/exchange/components/wallet-item/wallet-item.component';
import { ExchangeStore } from '@kr/components/exchange/exchange.store';
import { ExchangeForm } from '@kr/components/exchange/models';
import { isUsdtSymbol, WalletCard } from '@kr/models';

import { digitPattern } from '../../../../core/helpers/digit-pattern';

export const decimalLengthValidator = (control: AbstractControl, wallet: WalletCard) => {
  const value = control.value?.replaceAll(',', '.');

  if (value && value.includes && value.includes('.')) {
    const decimalPart = value.split('.')[1];
    if (decimalPart.length > Number(wallet?.coin.decimals)) {
      return { maxDecimals: true };
    }
  }
  return null;
};

@Component({
  selector: 'kr-exchange-set-crypto-crypto',
  imports: [
    SearchSelectComponent,
    SimpleInputModule,
    TranslateModule,
    IconModule,
    ReactiveFormsModule,
    TextAreaComponentModule,
    WalletItemComponent,
  ],
  templateUrl: './exchange-set-crypto-crypto.component.html',
  styleUrl: './exchange-set-crypto-crypto.component.scss',
})
export class ExchangeSetCryptoCryptoComponent implements OnInit {
  readonly destroyRef = inject(DestroyRef);
  readonly store = inject(ExchangeStore);

  form = input<FormGroup<GroupResolverFormBuilder<ExchangeForm>>>();
  wallets = input<WalletCard[]>();

  walletsReceive = signal<WalletCard[]>([]);

  ngOnInit(): void {
    this.form()
      .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.walletsReceive.set(
          this.wallets().filter(
            item =>
              this.form().value.walletFromId?.toString() !== item.id &&
              this.getWalletById(this.form().value.walletFromId?.toString())?.coin?.id !== item.coin.id,
          ),
        );
        this.setValidation();
      });
  }

  setValidation(): void {
    this.form().controls.amount.setValidators(
      [
        Validators.pattern(digitPattern),
        () => decimalLengthValidator(this.form().controls.amount, this.getWalletById(this.form().value.walletFromId?.toString())),
      ],
      { emitEvent: false },
    );

    this.form().controls.amountTo.setValidators(
      [
        Validators.pattern(digitPattern),
        () => decimalLengthValidator(this.form().controls.amountTo, this.getWalletById(this.form().value.walletToId?.toString())),
      ],
      { emitEvent: false },
    );
  }

  getWalletById(id: string | number): WalletCard {
    return this.wallets().find(wallet => wallet.id == id);
  }

  walletTransferMinAmount(wallet: WalletCard): number {
    return Number(wallet?.coin.minAmount);
  }

  isUsdt(wallet: WalletCard): boolean {
    const symbol = wallet.coin.symbol ?? wallet.secondaryWalletCoin.symbol;
    return isUsdtSymbol(symbol);
  }
}
